import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Search, X, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import AdminLayout from '../../components/admin/AdminLayout';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSelfImprovement = () => {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    category: '',
    title: '',
    description: '',
    content: '',
    recommendedBooks: ''
  });

  const categories = [
    'Personal Growth',
    'Marriage',
    'Physical Health',
    'Work-Life Balance',
    'Single Dad Self-Care',
    'Financial Wellness',
    'Male Friendship',
    'Anger Management',
    'Purpose and Legacy'
  ];

  useEffect(() => {
    fetchTopics();
  }, []);

  const fetchTopics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/self-improvement`);
      setTopics(response.data);
    } catch (error) {
      console.error('Error fetching topics:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTopics = topics.filter(topic =>
    topic.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    topic.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleEdit = (topic) => {
    setEditingTopic(topic);
    
    // Format recommended books
    let booksStr = '';
    if (topic.recommendedBooks && topic.recommendedBooks.length > 0) {
      booksStr = topic.recommendedBooks.map(book => 
        `${book.title}|||${book.author}|||${book.why || ''}`
      ).join('\n');
    }

    setFormData({
      category: topic.category,
      title: topic.title,
      description: topic.description,
      content: topic.content || '',
      recommendedBooks: booksStr
    });
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingTopic(null);
    setFormData({
      category: '',
      title: '',
      description: '',
      content: '',
      recommendedBooks: ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    // Parse recommended books
    const booksArray = formData.recommendedBooks.split('\n')
      .filter(b => b.trim())
      .map(book => {
        const [title, author, why] = book.split('|||');
        return {
          title: title?.trim() || '',
          author: author?.trim() || '',
          why: why?.trim() || ''
        };
      })
      .filter(b => b.title && b.author);

    const topicData = {
      category: formData.category,
      title: formData.title,
      description: formData.description,
      content: formData.content,
      recommendedBooks: booksArray
    };

    try {
      if (editingTopic) {
        await axios.put(`${API}/admin/self-improvement/${editingTopic.id}`, topicData);
      } else {
        await axios.post(`${API}/admin/self-improvement`, topicData);
      }
      setShowModal(false);
      fetchTopics();
    } catch (error) {
      console.error('Error saving topic:', error);
      alert('Failed to save topic. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (topicId) => {
    if (!window.confirm('Are you sure you want to delete this topic?')) return;

    try {
      await axios.delete(`${API}/admin/self-improvement/${topicId}`);
      fetchTopics();
    } catch (error) {
      console.error('Error deleting topic:', error);
      alert('Failed to delete topic.');
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Personal Growth': 'bg-purple-100 text-purple-800',
      'Marriage': 'bg-red-100 text-red-800',
      'Physical Health': 'bg-green-100 text-green-800',
      'Work-Life Balance': 'bg-blue-100 text-blue-800',
      'Single Dad Self-Care': 'bg-cyan-100 text-cyan-800',
      'Financial Wellness': 'bg-emerald-100 text-emerald-800',
      'Male Friendship': 'bg-amber-100 text-amber-800',
      'Anger Management': 'bg-orange-100 text-orange-800',
      'Purpose and Legacy': 'bg-violet-100 text-violet-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold">Self-Improvement</h1>
            <p className="text-gray-600">Manage self-improvement topics ({topics.length} total)</p>
          </div>
          <Button
            onClick={handleCreate}
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Topic
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <Input
            placeholder="Search topics..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Topics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTopics.map((topic) => (
            <Card key={topic.id} className="border-2 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-3">
                  <Badge className={getCategoryColor(topic.category)}>
                    {topic.category}
                  </Badge>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(topic)}
                      className="h-8 w-8 p-0"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(topic.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <h3 className="font-bold text-lg mb-2">{topic.title}</h3>
                <p className="text-gray-600 text-sm line-clamp-3">{topic.description}</p>
                {topic.recommendedBooks?.length > 0 && (
                  <p className="text-xs text-gray-400 mt-3">
                    {topic.recommendedBooks.length} recommended book{topic.recommendedBooks.length !== 1 ? 's' : ''}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white">
                <h2 className="text-2xl font-bold">
                  {editingTopic ? 'Edit Topic' : 'Create Topic'}
                </h2>
                <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Title *</label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Category *</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full p-2 border rounded-md"
                      required
                    >
                      <option value="">Select category</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Description *</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full p-2 border rounded-md"
                    rows={2}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Content *</label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    className="w-full p-2 border rounded-md"
                    rows={6}
                    required
                    placeholder="Full article content..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Recommended Books (one per line: Title|||Author|||Why recommend)
                  </label>
                  <textarea
                    value={formData.recommendedBooks}
                    onChange={(e) => setFormData({ ...formData, recommendedBooks: e.target.value })}
                    className="w-full p-2 border rounded-md font-mono text-sm"
                    rows={4}
                    placeholder="Emotional Intelligence 2.0|||Travis Bradberry|||Practical strategies with self-assessment tool&#10;The 5 Love Languages|||Gary Chapman|||Understanding how to show love effectively"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Format: "Book Title|||Author|||Why this book is recommended"
                  </p>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-purple-600 text-white" disabled={saving}>
                    {saving ? 'Saving...' : (editingTopic ? 'Update Topic' : 'Create Topic')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSelfImprovement;
